/**
 * useLiveClassRtc — WebRTC mesh client hook for Live Classroom.
 *
 * One peer connection per remote participant. The hook hides the SDP /
 * ICE plumbing: callers see a list of `peers` with `stream` objects ready
 * to bind to <video srcObject>, plus toggle helpers for camera / mic / screen.
 *
 * Topology: full mesh (Phase 2). Up to ~8 publishers comfortably; everyone
 * else can stay audio-only or text-only. Phase 3 will route through an SFU
 * without changing this hook's external surface.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { describeCameraError } from '@/lib/cameraAccess';
import { getToken } from './authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const randPeerId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
};

export function useLiveClassRtc({ sessionId, enabled = true, mesh = true }) {
  // `mesh: false` keeps this socket as the lesson's presence, register and
  // chat channel while the picture travels through the streaming server
  // instead of peer-to-peer. Dropping the socket would drop the register.
  const [peers, setPeers] = useState({});       // peer_id -> {profile, state, stream}
  const [localStream, setLocalStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [connected, setConnected] = useState(false);
  const [myPeerId] = useState(() => randPeerId());
  const [mediaState, setMediaState] = useState({ camera: false, mic: false, screen: false });
  const [error, setError] = useState(null);
  const [errorSteps, setErrorSteps] = useState([]);
  // Set by the server when we join: only the teacher taking this lesson (or a
  // school admin) may present. The buttons follow this, and the relay enforces
  // it regardless of what the buttons do.
  const [canPublish, setCanPublish] = useState(false);

  const wsRef = useRef(null);
  const pcsRef = useRef({});                    // peer_id -> RTCPeerConnection
  const localStreamRef = useRef(null);
  const polRef = useRef({});                    // peer_id -> "polite"|"impolite" (for renegotiation)
  const sendQueue = useRef([]);

  // --- helpers ---
  const wsSend = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
    else sendQueue.current.push(msg);
  }, []);

  const flushQueue = () => {
    const q = sendQueue.current;
    sendQueue.current = [];
    for (const m of q) wsSend(m);
  };

  const makePc = useCallback((peerId, polite) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    polRef.current[peerId] = polite ? 'polite' : 'impolite';

    pc.onicecandidate = (e) => {
      if (e.candidate) wsSend({ type: 'ice', target: peerId, candidate: e.candidate.toJSON() });
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      setPeers((prev) => ({
        ...prev,
        [peerId]: { ...(prev[peerId] || {}), stream },
      }));
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') {
        // try a quick restart
        try { pc.restartIce(); } catch { /* ignore */ }
      }
    };
    pc.onnegotiationneeded = async () => {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        wsSend({ type: 'offer', target: peerId, sdp: pc.localDescription });
      } catch (e) {
        // ignore
      }
    };

    // Add any existing local tracks
    const ls = localStreamRef.current;
    if (ls) {
      for (const track of ls.getTracks()) pc.addTrack(track, ls);
    }
    pcsRef.current[peerId] = pc;
    return pc;
  }, [wsSend]);

  const closePc = (peerId) => {
    const pc = pcsRef.current[peerId];
    if (pc) {
      try { pc.close(); } catch { /* ignore */ }
    }
    delete pcsRef.current[peerId];
    setPeers((prev) => {
      const { [peerId]: _, ...rest } = prev;
      return rest;
    });
  };

  // --- WS lifecycle ---
  useEffect(() => {
    if (!enabled || !sessionId) return;
    const token = getToken();
    if (!token) { setError('Not authenticated'); return; }

    const wsUrl = API_URL.replace(/^http/, 'ws') + `/api/ws/live-class/${sessionId}?token=${encodeURIComponent(token)}&peer_id=${encodeURIComponent(myPeerId)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => { setConnected(true); flushQueue(); };
    ws.onerror = () => { setError('Signaling error'); };
    ws.onclose = () => { setConnected(false); };

    ws.onmessage = async (evt) => {
      let msg; try { msg = JSON.parse(evt.data); } catch { return; }
      switch (msg.type) {
        case 'joined': {
          // Only the teacher taking this lesson (or a school admin) may
          // present; the server decides, not the page.
          setCanPublish(!!msg.can_publish);
          setCanPublish(!!msg.can_publish);
          // Existing peers already in room; we are "impolite" (we initiate)
          for (const p of msg.peers || []) {
            setPeers((prev) => ({ ...prev, [p.peer_id]: { profile: p, state: p, stream: null } }));
            if (!mesh) continue;
            const pc = makePc(p.peer_id, /* polite */ false);
            // Trigger negotiation by adding a transceiver if no tracks yet
            if (pc.getTransceivers().length === 0) {
              pc.addTransceiver('audio', { direction: 'recvonly' });
              pc.addTransceiver('video', { direction: 'recvonly' });
            }
          }
          break;
        }
        case 'peer_joined': {
          // A new peer arrived; they will send us an offer ("polite" — we answer)
          setPeers((prev) => ({ ...prev, [msg.peer_id]: { profile: msg.profile, state: msg.profile, stream: null } }));
          if (mesh) makePc(msg.peer_id, /* polite */ true);
          break;
        }
        case 'peer_left': {
          closePc(msg.peer_id);
          break;
        }
        case 'offer': {
          if (!mesh) break;
          let pc = pcsRef.current[msg.from];
          if (!pc) pc = makePc(msg.from, /* polite */ true);
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            wsSend({ type: 'answer', target: msg.from, sdp: pc.localDescription });
          } catch (e) { /* ignore */ }
          break;
        }
        case 'answer': {
          const pc = pcsRef.current[msg.from];
          if (pc) {
            try { await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp)); }
            catch { /* ignore */ }
          }
          break;
        }
        case 'ice': {
          const pc = pcsRef.current[msg.from];
          if (pc) {
            try { await pc.addIceCandidate(new RTCIceCandidate(msg.candidate)); }
            catch { /* may arrive before remote desc — best effort */ }
          }
          break;
        }
        case 'media_state': {
          setPeers((prev) => ({
            ...prev,
            [msg.peer_id]: { ...(prev[msg.peer_id] || {}), state: msg.state },
          }));
          break;
        }
        default:
          break;
      }
    };

    return () => {
      try { ws.send(JSON.stringify({ type: 'leave' })); } catch { /* ignore */ }
      try { ws.close(); } catch { /* ignore */ }
      wsRef.current = null;
      // Tear down all peer connections
      for (const id of Object.keys(pcsRef.current)) closePc(id);
      // Stop our local devices
      const s = localStreamRef.current;
      if (s) for (const t of s.getTracks()) t.stop();
      localStreamRef.current = null;
      setLocalStream(null);
      setScreenStream(null);
      setMediaState({ camera: false, mic: false, screen: false });
      setPeers({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionId]);

  // --- Local media controls ---
  const ensureLocalStream = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const s = new MediaStream();
    localStreamRef.current = s;
    setLocalStream(s);
    return s;
  }, []);

  const replaceTrackOnAll = (kind, newTrack) => {
    for (const pc of Object.values(pcsRef.current)) {
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === kind);
      if (sender) {
        if (newTrack) sender.replaceTrack(newTrack);
      } else if (newTrack) {
        pc.addTrack(newTrack, localStreamRef.current);
      }
    }
  };

  const toggleCamera = useCallback(async () => {
    const s = await ensureLocalStream();
    const existing = s.getVideoTracks().find((t) => !t.label.toLowerCase().includes('screen'));
    if (existing) {
      existing.stop();
      s.removeTrack(existing);
      replaceTrackOnAll('video', null);
      // Also stop sending video
      for (const pc of Object.values(pcsRef.current)) {
        const sender = pc.getSenders().find((sn) => sn.track && sn.track.kind === 'video');
        if (sender) try { pc.removeTrack(sender); } catch { /* ignore */ }
      }
      setMediaState((m) => ({ ...m, camera: false }));
      wsSend({ type: 'media_state', camera: false });
      return;
    }
    try {
      setError(null);
      setErrorSteps([]);
      const cam = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      const vt = cam.getVideoTracks()[0];
      s.addTrack(vt);
      for (const pc of Object.values(pcsRef.current)) pc.addTrack(vt, s);
      setMediaState((m) => ({ ...m, camera: true }));
      wsSend({ type: 'media_state', camera: true });
    } catch (e) {
      // Was "Camera blocked or unavailable", which tells a teacher on a phone
      // nothing. The shared helper names the operating system setting and the
      // browser setting, per platform.
      const { message, steps } = describeCameraError(e);
      setError(message);
      setErrorSteps(steps);
    }
  }, [ensureLocalStream, wsSend]);

  const toggleMic = useCallback(async () => {
    const s = await ensureLocalStream();
    const existing = s.getAudioTracks()[0];
    if (existing) {
      existing.stop();
      s.removeTrack(existing);
      for (const pc of Object.values(pcsRef.current)) {
        const sender = pc.getSenders().find((sn) => sn.track && sn.track.kind === 'audio');
        if (sender) try { pc.removeTrack(sender); } catch { /* ignore */ }
      }
      setMediaState((m) => ({ ...m, mic: false }));
      wsSend({ type: 'media_state', mic: false });
      return;
    }
    try {
      const a = await navigator.mediaDevices.getUserMedia({ audio: true });
      const at = a.getAudioTracks()[0];
      s.addTrack(at);
      for (const pc of Object.values(pcsRef.current)) pc.addTrack(at, s);
      setMediaState((m) => ({ ...m, mic: true }));
      wsSend({ type: 'media_state', mic: true });
    } catch (e) {
      const { message, steps } = describeCameraError(e);
      setError(message.replace('camera', 'microphone'));
      setErrorSteps(steps);
    }
  }, [ensureLocalStream, wsSend]);

  const toggleScreen = useCallback(async () => {
    if (mediaState.screen) {
      // stop current screen tracks
      const cur = screenStream;
      if (cur) {
        for (const t of cur.getTracks()) t.stop();
      }
      setScreenStream(null);
      setMediaState((m) => ({ ...m, screen: false }));
      wsSend({ type: 'media_state', screen: false });
      // re-attach camera if it was on
      // (camera track is independent so already on senders)
      return;
    }
    try {
      const ss = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      setScreenStream(ss);
      const vt = ss.getVideoTracks()[0];
      // Replace the video track on every peer with the screen track
      for (const pc of Object.values(pcsRef.current)) {
        const sender = pc.getSenders().find((sn) => sn.track && sn.track.kind === 'video');
        if (sender) sender.replaceTrack(vt);
        else pc.addTrack(vt, ss);
      }
      vt.onended = () => {
        // User clicked the "Stop sharing" native browser UI
        setScreenStream(null);
        setMediaState((m) => ({ ...m, screen: false }));
        wsSend({ type: 'media_state', screen: false });
        // restore camera if we still have one
        const camTrack = localStreamRef.current?.getVideoTracks().find((t) => !t.label.toLowerCase().includes('screen'));
        if (camTrack) {
          for (const pc of Object.values(pcsRef.current)) {
            const sender = pc.getSenders().find((sn) => sn.track && sn.track.kind === 'video');
            if (sender) sender.replaceTrack(camTrack);
          }
        }
      };
      setMediaState((m) => ({ ...m, screen: true }));
      wsSend({ type: 'media_state', screen: true });
    } catch (e) {
      setError('Screen share cancelled');
    }
  }, [mediaState.screen, screenStream, wsSend]);

  // Decorate peers with display info from media_state we got at join
  const peerList = useMemo(() => Object.entries(peers).map(([id, p]) => ({
    peer_id: id,
    name: p.state?.name || p.profile?.name || 'Participant',
    role: p.state?.role || p.profile?.role,
    camera: !!p.state?.camera,
    mic: !!p.state?.mic,
    screen: !!p.state?.screen,
    stream: p.stream || null,
  })), [peers]);

  return {
    connected, myPeerId, error, errorSteps, canPublish,
    localStream, screenStream,
    mediaState, peers: peerList,
    toggleCamera, toggleMic, toggleScreen,
  };
}
